<div class="channel-types-container">
  <!-- Header -->
  <div class="section-header">
    <div class="header-content">
      <h2>Channel Types</h2>
      <p class="section-description">Define communication channel types and their providers</p>
    </div>
    <button class="btn btn-primary" (click)="startCreate()" [disabled]="isCreating || isSaving">
      <i class="fas fa-plus"></i> Add Channel Type
    </button>
  </div>

  <!-- Messages -->
  <div class="message error-message" *ngIf="errorMessage">
    <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
  </div>
  <div class="message success-message" *ngIf="successMessage">
    <i class="fas fa-check-circle"></i> {{ successMessage }}
  </div>

  <!-- Loading State -->
  <div class="loading-container" *ngIf="isLoading">
    <i class="fas fa-spinner fa-spin"></i> Loading channel types...
  </div>

  <!-- Search -->
  <div class="filters-row" *ngIf="!isLoading">
    <div class="search-box">
      <i class="fas fa-search"></i>
      <input type="text" [(ngModel)]="searchQuery" placeholder="Search channel types..." />
    </div>
  </div>

  <!-- Create Form -->
  <div class="create-form card" *ngIf="isCreating">
    <h3>New Channel Type</h3>
    <div class="form-grid">
      <div class="form-group">
        <label>Name *</label>
        <input type="text" [(ngModel)]="createForm.name" placeholder="e.g., Email, Slack, SMS" />
      </div>
      <div class="form-group">
        <label>Communication Provider</label>
        <select [(ngModel)]="createForm.communicationProviderID">
          <option value="">Select a provider...</option>
          <option *ngFor="let provider of providers" [value]="provider.ID">{{ provider.Name }}</option>
        </select>
      </div>
      <div class="form-group full-width">
        <label>Description</label>
        <textarea [(ngModel)]="createForm.description" placeholder="Describe this channel type" rows="2"></textarea>
      </div>
      <div class="form-group">
        <label>Action Inherit Mode</label>
        <select [(ngModel)]="createForm.actionInheritMode">
          <option value="Organization">Inherit from Organization</option>
          <option value="ChannelType">Channel Type Only</option>
          <option value="Combined">Combined (Org + Type)</option>
        </select>
        <small class="help-text">How actions are inherited at this level</small>
      </div>
      <div class="form-group">
        <label>Status</label>
        <select [(ngModel)]="createForm.status">
          <option value="Active">Active</option>
          <option value="Pending">Pending</option>
          <option value="Revoked">Revoked</option>
        </select>
      </div>
    </div>
    <div class="form-actions">
      <button class="btn btn-secondary" (click)="cancelCreate()" [disabled]="isSaving">Cancel</button>
      <button class="btn btn-primary" (click)="saveCreate()" [disabled]="isSaving">
        <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
        {{ isSaving ? 'Saving...' : 'Create Channel Type' }}
      </button>
    </div>
  </div>

  <!-- Channel Types Grid -->
  <div class="channel-types-grid" *ngIf="!isLoading">
    <div class="channel-type-card card" *ngFor="let ctd of filteredChannelTypes" [class.editing]="ctd.isEditing">
      <!-- View Mode -->
      <ng-container *ngIf="!ctd.isEditing">
        <div class="card-header">
          <div class="type-icon">
            <i class="fas" [ngClass]="getProviderIcon(ctd.provider?.Name || '')"></i>
          </div>
          <span class="status-badge" [ngClass]="getStatusClass(ctd.channelType.Status)">
            {{ ctd.channelType.Status }}
          </span>
        </div>
        <h3 class="type-name">{{ ctd.channelType.Name }}</h3>
        <p class="type-description">{{ ctd.channelType.Description || 'No description' }}</p>
        <div class="type-meta">
          <div class="meta-item" *ngIf="ctd.provider">
            <i class="fas fa-plug"></i>
            <span>{{ ctd.provider.Name }}</span>
          </div>
          <div class="meta-item">
            <i class="fas fa-sitemap"></i>
            <span>{{ getInheritModeLabel(ctd.channelType.ActionInheritMode) }}</span>
          </div>
        </div>
        <div class="card-actions">
          <button class="btn btn-secondary" (click)="startEdit(ctd)" [disabled]="isSaving">
            <i class="fas fa-edit"></i> Edit
          </button>
          <button class="btn btn-icon btn-danger" title="Delete" (click)="confirmDelete(ctd)" [disabled]="isSaving">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </ng-container>

      <!-- Edit Mode -->
      <ng-container *ngIf="ctd.isEditing">
        <div class="edit-form">
          <div class="form-group">
            <label>Name</label>
            <input type="text" [(ngModel)]="ctd.editForm.name" />
          </div>
          <div class="form-group">
            <label>Communication Provider</label>
            <select [(ngModel)]="ctd.editForm.communicationProviderID">
              <option value="">Select a provider...</option>
              <option *ngFor="let provider of providers" [value]="provider.ID">{{ provider.Name }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>Description</label>
            <textarea [(ngModel)]="ctd.editForm.description" rows="2"></textarea>
          </div>
          <div class="form-group">
            <label>Action Inherit Mode</label>
            <select [(ngModel)]="ctd.editForm.actionInheritMode">
              <option value="Organization">Inherit from Organization</option>
              <option value="ChannelType">Channel Type Only</option>
              <option value="Combined">Combined (Org + Type)</option>
            </select>
          </div>
          <div class="form-group">
            <label>Status</label>
            <select [(ngModel)]="ctd.editForm.status">
              <option value="Active">Active</option>
              <option value="Pending">Pending</option>
              <option value="Revoked">Revoked</option>
            </select>
          </div>
          <div class="form-actions">
            <button class="btn btn-secondary" (click)="cancelEdit(ctd)" [disabled]="isSaving">Cancel</button>
            <button class="btn btn-primary" (click)="saveEdit(ctd)" [disabled]="isSaving">
              <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
              Save
            </button>
          </div>
        </div>
      </ng-container>
    </div>

    <!-- Empty State -->
    <div class="empty-state card" *ngIf="filteredChannelTypes.length === 0 && !isCreating">
      <i class="fas fa-broadcast-tower"></i>
      <p>No channel types defined</p>
      <button class="btn btn-primary" (click)="startCreate()">Create Your First Channel Type</button>
    </div>
  </div>

  <!-- Confirm Dialog -->
  <div class="modal-overlay" *ngIf="confirmDialogVisible" (click)="closeConfirmDialog()">
    <div class="modal-dialog" (click)="$event.stopPropagation()">
      <h3>{{ confirmDialogTitle }}</h3>
      <p>{{ confirmDialogMessage }}</p>
      <div class="modal-actions">
        <button class="btn btn-secondary" (click)="closeConfirmDialog()">Cancel</button>
        <button class="btn btn-danger" (click)="confirmDialogYes()" [disabled]="isSaving">
          <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
          Delete
        </button>
      </div>
    </div>
  </div>
</div>
